import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, createContext, useContext, useLayoutEffect } from "react";
import BottomNav from "@/components/BottomNav";
import InstallPrompt from "@/components/InstallPrompt";

// ScrollToTop component - ensures all pages start at the top
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useLayoutEffect(() => {
    // Scroll to top immediately on route change
    window.scrollTo(0, 0);
    // Also reset any scroll containers
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname]);
  
  return null;
}

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import AddTool from "@/pages/AddTool";
import ToolDetail from "@/pages/ToolDetail";
import Profile from "@/pages/Profile";
import AuthCallback from "@/pages/AuthCallback";
import Notifications from "@/pages/Notifications";
import News from "@/pages/News";
import NewsStory from "@/pages/NewsStory";
import Contact from "@/pages/Contact";
import Insurance from "@/pages/Insurance";
import ToolInsureCo from "@/pages/ToolInsureCo";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminTools from "@/pages/admin/AdminTools";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminPolice from "@/pages/admin/AdminPolice";
import AdminNews from "@/pages/admin/AdminNews";
import AdminBackups from "@/pages/admin/AdminBackups";
import AdminCaseFiles from "@/pages/admin/AdminCaseFiles";
import AdminNotifications from "@/pages/admin/AdminNotifications";
import PoliceLogin from "@/pages/police/PoliceLogin";
import PolicePortal from "@/pages/police/PolicePortal";
import PublicToolCheck from "@/pages/PublicToolCheck";
import VerifyTool from "@/pages/VerifyTool";
import MockupShield from "@/pages/MockupShield";
import MockupSawblade from "@/pages/MockupSawblade";
import MockupBolt from "@/pages/MockupBolt";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import BrandKit from "@/pages/BrandKit";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id check
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/mockup/shield" element={<MockupShield />} />
      <Route path="/mockup/sawblade" element={<MockupSawblade />} />
      <Route path="/mockup/bolt" element={<MockupBolt />} />
      <Route path="/brand-kit" element={<BrandKit />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/check" element={<PublicToolCheck />} />
      <Route path="/verify/:toolId" element={<VerifyTool />} />
      <Route path="/news" element={<News />} />
      <Route path="/news/:slug" element={<NewsStory />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/insurance" element={<Insurance />} />
      <Route path="/toolinsureco" element={<ToolInsureCo />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/add-tool" element={<ProtectedRoute><AddTool /></ProtectedRoute>} />
      <Route path="/tool/:toolId" element={<ProtectedRoute><ToolDetail /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/tools" element={<ProtectedRoute><AdminTools /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/logs" element={<ProtectedRoute><AdminLogs /></ProtectedRoute>} />
      <Route path="/admin/police" element={<ProtectedRoute><AdminPolice /></ProtectedRoute>} />
      <Route path="/admin/news" element={<ProtectedRoute><AdminNews /></ProtectedRoute>} />
      <Route path="/admin/backups" element={<ProtectedRoute><AdminBackups /></ProtectedRoute>} />
      <Route path="/admin/cases" element={<ProtectedRoute><AdminCaseFiles /></ProtectedRoute>} />
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotifications /></ProtectedRoute>} />
      <Route path="/police" element={<PoliceLogin />} />
      <Route path="/police/portal" element={<PolicePortal />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      // Try to get session token from localStorage (fallback for iOS Safari)
      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/auth/me`, {
        headers
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        // Clear stored token if invalid
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (userData, sessionToken) => {
    setUser(userData);
    // Store session token in localStorage for iOS Safari fallback
    if (sessionToken) {
      localStorage.setItem('session_token', sessionToken);
    }
  };

  const logout = async () => {
    try {
      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    localStorage.removeItem('session_token');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      <div className="App noise-bg">
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <BottomNav isLoggedIn={!!user} />
          <InstallPrompt />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
